import { useEffect, useRef, useState } from 'react';

/**
 * Page one of a PDF, as a thumbnail — the browser's own PDF viewer in an `<embed>`, scaled into the well.
 *
 * No pdf.js: adding a renderer dependency for a thumbnail is not worth the bytes or the compliance
 * surface, and every target browser already renders a PDF natively. Page-one-only is a limitation
 * everywhere else (it is why the full viewer's embed is desktop-gated) and is exactly what a thumbnail
 * wants.
 *
 * The guard that makes this affordable: an embed spawns the browser's PDF machinery, so one per card in a
 * grid is real cost. It mounts ONLY once the card has scrolled into view, and stays mounted after — an
 * IntersectionObserver rather than rendering all of them up front. Shared in intent with the native
 * `PdfThumbnail`s, which cache a raster instead.
 */
export function PdfThumb({ src, fallback }: { src: string; fallback: React.ReactNode }) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const el = hostRef.current;
    if (!el || visible || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setVisible(true);
        observer.disconnect();
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible]);

  return (
    <div
      ref={hostRef}
      style={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        background: 'var(--ds-white)',
        // Inherit the well's rounding: the embed is a replaced element and paints over square corners
        // otherwise, which showed as the card's corners overflowing.
        borderRadius: 'inherit',
      }}
    >
      {visible && !failed ? (
        <embed
          // `#toolbar=0` and friends are viewer hints, not guarantees — a browser that ignores them just
          // shows its own chrome, which is still a readable page one.
          src={`${src}#page=1&toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
          type="application/pdf"
          // Inert: the card's own click owns the tap, and a scrollable embed inside a scrolling grid would
          // trap the wheel.
          // Slightly oversized and centred so the viewer's own 1px edge/toolbar chrome is cropped by the
          // host rather than drawn as a line across the card.
          style={{
            width: 'calc(100% + 4px)',
            height: 'calc(100% + 4px)',
            margin: -2,
            border: 'none',
            pointerEvents: 'none',
            display: 'block',
          }}
          onError={() => setFailed(true)}
          aria-hidden
        />
      ) : (
        fallback
      )}
    </div>
  );
}
